import React, { useEffect, useState, useMemo } from "react";
import {
  FiArrowUpRight,
  FiCalendar,
  FiClock,
  FiSearch,
  FiBriefcase,
  FiUsers,
  FiX,
} from "react-icons/fi";
import TopBanner from "../../../components/topBanner/topBanner";
import imgBanner from "../../../images/banner/banner-tin-tuc.jpg";
import TitleCenter from "../../../components/title/TitleCenter";
import {
  getRecruitmentsByLanguage,
  getRecruitmentDepartmentsByLanguage,
  getRecruitmentJobTypesByLanguage,
} from "../../../apis/recruitments";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { normalizeLang } from "../../../utils/localeDataLoader";

const formatRecruitmentDate = (value, languageCode) => {
  if (!value) return "";

  return new Date(value).toLocaleDateString(
    languageCode === "vi" ? "vi-VN" : "en-US",
  );
};

export default function Recruitment() {
  const [products, setProducts] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);

  // Filter state
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("ALL");
  const [selectedJobType, setSelectedJobType] = useState("ALL");

  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const languageCode = normalizeLang(i18n.language);
    const expectedBasePath =
      languageCode === "vi"
        ? "/tuyen-dung/cong-viec-tuyen-dung"
        : "/recruitment/career-opportunities";
    const currentPath = location.pathname;

    if (
      currentPath === "/tuyen-dung" ||
      currentPath === "/tuyen-dung/" ||
      currentPath === "/recruitment" ||
      currentPath === "/recruitment/" ||
      currentPath === "/tuyen-dung/cong-viec-tuyen-dung" ||
      currentPath === "/tuyen-dung/cong-viec-tuyen-dung/" ||
      currentPath === "/recruitment/career-opportunities" ||
      currentPath === "/recruitment/career-opportunities/"
    ) {
      if (!currentPath.startsWith(expectedBasePath)) {
        navigate(expectedBasePath, { replace: true });
      }
    }
  }, [i18n.language, location.pathname, navigate]);

  useEffect(() => {
    const languageCode = normalizeLang(i18n.language);

    Promise.all([
      getRecruitmentsByLanguage(languageCode),
      getRecruitmentDepartmentsByLanguage(languageCode),
      getRecruitmentJobTypesByLanguage(languageCode),
    ])
      .then(([jobsRes, deptsRes, jobTypesRes]) => {
        if (jobsRes?.data?.items) {
          const now = Date.now();
          const mappedData = [...jobsRes.data.items]
            .filter(item => !item.expiresAt || new Date(item.expiresAt).getTime() >= now)
            .sort((a, b) => {
              const dateA = a.publishedAt
                ? new Date(a.publishedAt).getTime()
                : 0;
              const dateB = b.publishedAt
                ? new Date(b.publishedAt).getTime()
                : 0;

              return dateB - dateA;
            })
            .map((item, index) => ({
              id: index + 1,
              originalId: item.id,
              slug: item.slug,
              location: item.title,
              department: item.department,
              departmentCode: item.departmentCode,
              jobType: item.jobType,
              jobTypeCode: item.jobTypeCode,
              dateup: formatRecruitmentDate(item.publishedAt, languageCode),
              dateout: formatRecruitmentDate(item.expiresAt, languageCode),
            }));

          setProducts(mappedData);
        }

        if (deptsRes?.data) {
          setDepartments(deptsRes.data);
        }

        if (jobTypesRes?.data) {
          setJobTypes(jobTypesRes.data);
        }
      })
      .catch((error) =>
        console.error("Failed to fetch recruitments data", error),
      );
  }, [i18n.language]);

  const openRecruitment = (recruitment) => {
    const languageCode = normalizeLang(i18n.language);
    const basePath =
      languageCode === "vi"
        ? "tuyen-dung/cong-viec-tuyen-dung"
        : "recruitment/career-opportunities";
    navigate(`/${basePath}/${recruitment.slug}`);
  };

  const isFiltered =
    searchKeyword.trim() !== "" ||
    selectedDepartment !== "ALL" ||
    selectedJobType !== "ALL";

  const clearFilters = () => {
    setSearchKeyword("");
    setSelectedDepartment("ALL");
    setSelectedJobType("ALL");
  };

  const filteredProducts = useMemo(() => {
    const kw = searchKeyword.trim().toLowerCase();
    return products.filter((job) => {
      const matchKeyword =
        !kw ||
        job.location?.toLowerCase().includes(kw) ||
        job.department?.toLowerCase().includes(kw) ||
        job.jobType?.toLowerCase().includes(kw);

      const matchDept =
        selectedDepartment === "ALL" ||
        job.departmentCode === selectedDepartment;

      const matchJobType =
        selectedJobType === "ALL" ||
        job.jobTypeCode === selectedJobType;

      return matchKeyword && matchDept && matchJobType;
    });
  }, [products, searchKeyword, selectedDepartment, selectedJobType]);

  return (
    <div className="Recruitment">
      <TopBanner
        titleCenter={t("recruitment.title")}
        imgBanner={imgBanner}
        pageName={t("recruitment.title")}
      />
      <main className="layout_container recruitment-board">
        <TitleCenter text={t("recruitment.title")} />

        {/* VNG / Dribbble Inspired Filter Bar */}
        <div className="recruitment-filter-bar">
          <div className="recruitment-filter-bar__search">
            <FiSearch
              className="recruitment-filter-bar__search-icon"
              aria-hidden="true"
            />
            <input
              type="text"
              placeholder={t("recruitment.search_placeholder")}
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              className="recruitment-filter-bar__search-input"
            />
            {searchKeyword && (
              <button
                type="button"
                className="recruitment-filter-bar__search-clear"
                onClick={() => setSearchKeyword("")}
                title={t("recruitment.clear_filter")}
              >
                <FiX />
              </button>
            )}
          </div>

          <div className="recruitment-filter-bar__dropdowns">
            <div className="recruitment-filter-bar__select-wrapper">
              <FiUsers
                className="recruitment-filter-bar__select-icon"
                aria-hidden="true"
              />
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="recruitment-filter-bar__select"
              >
                <option value="ALL">{t("recruitment.department_all")}</option>
                {departments.map((dept) => (
                  <option key={dept.code} value={dept.code}>
                    {dept.title || dept.code}
                  </option>
                ))}
              </select>
            </div>

            <div className="recruitment-filter-bar__select-wrapper">
              <FiBriefcase
                className="recruitment-filter-bar__select-icon"
                aria-hidden="true"
              />
              <select
                value={selectedJobType}
                onChange={(e) => setSelectedJobType(e.target.value)}
                className="recruitment-filter-bar__select"
              >
                <option value="ALL">{t("recruitment.job_type_all")}</option>
                {jobTypes.map((jt) => (
                  <option key={jt.code} value={jt.code}>
                    {jt.title || jt.code}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Summary and Reset Bar */}
        <div className="recruitment-filter-summary">
          <div className="recruitment-filter-summary__count">
            <span className="recruitment-filter-summary__count-num">
              {filteredProducts.length}
            </span>
            <span className="recruitment-filter-summary__count-text">
              {t("recruitment.total_jobs")}
            </span>
          </div>
          {isFiltered && (
            <button
              type="button"
              className="recruitment-filter-summary__reset"
              onClick={clearFilters}
            >
              <FiX /> {t("recruitment.clear_filter")}
            </button>
          )}
        </div>

        {/* Job Cards List */}
        <div className="recruitment-board__list">
          {filteredProducts.length > 0 ? (
            filteredProducts.map((recruitment, index) => (
              <article
                className="recruitment-board__card"
                key={recruitment.originalId || recruitment.id}
                onClick={() => openRecruitment(recruitment)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    openRecruitment(recruitment);
                  }
                }}
                role="link"
                tabIndex={0}
              >
                <span className="recruitment-board__index" aria-hidden="true">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <div className="recruitment-board__role">
                  <div className="recruitment-board__badges">
                    {recruitment.department && (
                      <span className="recruitment-board__badge recruitment-board__badge--dept">
                        <FiUsers className="recruitment-board__badge-icon" />
                        {recruitment.department}
                      </span>
                    )}
                    {recruitment.jobType && (
                      <span className="recruitment-board__badge recruitment-board__badge--jobtype">
                        <FiBriefcase className="recruitment-board__badge-icon" />
                        {recruitment.jobType}
                      </span>
                    )}
                  </div>
                  <h2>{recruitment.location}</h2>
                  <span
                    className="recruitment-board__role-line"
                    aria-hidden="true"
                  />
                </div>
                <div className="recruitment-board__meta">
                  <div>
                    <FiCalendar aria-hidden="true" />
                    <span>{t("recruitment.dateup")}</span>
                    <strong>{recruitment.dateup}</strong>
                  </div>
                  <div>
                    <FiClock aria-hidden="true" />
                    <span>{t("recruitment.dateout")}</span>
                    <strong>{recruitment.dateout}</strong>
                  </div>
                </div>
                <span className="recruitment-board__cta">
                  {t("recruitment.view_detail")}
                  <FiArrowUpRight aria-hidden="true" />
                </span>
              </article>
            ))
          ) : (
            <div className="recruitment-board__empty">
              <div className="recruitment-board__empty-icon">
                <FiSearch />
              </div>
              <p className="recruitment-board__empty-text">
                {t("recruitment.no_jobs")}
              </p>
              {isFiltered && (
                <button
                  type="button"
                  className="recruitment-board__empty-reset"
                  onClick={clearFilters}
                >
                  <FiX /> {t("recruitment.clear_filter")}
                </button>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
